import { Edit, Plus, Save } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../../../../components/button";
import { Dropdown } from "../../../../components";
import { Input } from "../../../../components/input";
import { Label } from "../../../../components/label";
import { Textarea } from "../../../../components/textarea";
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGN_STATUSES,
  normalizeCampaignStatus,
} from "../../types/campaign-status.constants";

export interface ProgramFormValues {
  name: string;
  description: string;
  status: CAMPAIGN_STATUSES;
}

const programFormSchema = z.object({
  name: z.string().trim().min(1, "Program name is required."),
  description: z.string().trim(),
  status: z.nativeEnum(CAMPAIGN_STATUSES),
});

interface ProgramEditorProps {
  selectedProgram: {
    name: string;
    description?: string;
    status: string;
  } | null;
  onSubmit: (values: ProgramFormValues) => void | Promise<void>;
}

interface FieldLabelProps {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}

function FieldLabel({ htmlFor, children, required = false }: FieldLabelProps) {
  return (
    <Label
      htmlFor={htmlFor}
      className="inline-flex min-h-6 items-center gap-1 whitespace-nowrap text-sm font-medium leading-none text-[#0F172A]"
    >
      <span>{children}</span>
      {required ? <span className="text-red-600">*</span> : null}
    </Label>
  );
}

export function ProgramEditor({ selectedProgram, onSubmit }: ProgramEditorProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProgramFormValues>({
    defaultValues: {
      name: "",
      description: "",
      status: CAMPAIGN_STATUSES.Unactive,
    },
    resolver: zodResolver(programFormSchema),
  });

  const handleSubmitProgram = async (values: ProgramFormValues) => {
    await onSubmit(values);
    reset();
  };

  useEffect(() => {
    const selectedStatus = Object.values(CAMPAIGN_STATUSES).includes(
      selectedProgram?.status as CAMPAIGN_STATUSES,
    )
      ? normalizeCampaignStatus(selectedProgram?.status)
      : CAMPAIGN_STATUSES.Unactive;

    reset({
      name: selectedProgram?.name || "",
      description: selectedProgram?.description || "",
      status: selectedStatus,
    });
  }, [reset, selectedProgram]);

  return (
    <form
      className="rounded-2xl border border-[#DDEBE3] bg-white p-5 shadow-[0px_10px_24px_rgba(0,66,50,0.05)]"
      onSubmit={handleSubmit(handleSubmitProgram)}
    >
      <div className="mb-6 flex items-start gap-3 border-b border-[#E6EFE9] pb-4">
        <div className="rounded-xl bg-[#E8FFF0] p-2.5 text-[#00A86B]">
          {selectedProgram ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
        </div>
        <div>
          <h3 className="text-base font-semibold text-[#004232]">
            {selectedProgram ? "Edit Program" : "Create Program"}
          </h3>
          <p className="mt-1 text-sm text-[#6A7282]">
            {selectedProgram
              ? "Update the program details and save your changes."
              : "Add a program to organize campaign opportunities."}
          </p>
        </div>
      </div>

      <div className="w-full max-w-none space-y-5">
        <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="space-y-2">
            <FieldLabel
              htmlFor="program-name"
              required
            >
              Program Name
            </FieldLabel>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  id="program-name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Program name"
                  className="h-11 rounded-xl"
                />
              )}
            />
            {errors.name ? (
              <p className="text-xs text-red-600">{errors.name.message?.toString()}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <FieldLabel required>Status</FieldLabel>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  onValueChange={(value) => field.onChange(value as CAMPAIGN_STATUSES)}
                  options={Object.values(CAMPAIGN_STATUSES).map((option) => ({
                    label: CAMPAIGN_STATUS_LABELS[option as CAMPAIGN_STATUSES],
                    value: option,
                  }))}
                  placeholder="Select status"
                  triggerClassName="h-11 rounded-xl"
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="program-description">Description</FieldLabel>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                id="program-description"
                value={field.value}
                onChange={field.onChange}
                placeholder="Program description"
                className="min-h-[112px] rounded-xl"
              />
            )}
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end border-t border-[#E6EFE9] pt-4">
        <Button
          variant="primary"
          type="submit"
          className="min-w-36 gap-2"
        >
          {selectedProgram ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {selectedProgram ? "Save Program" : "Create Program"}
        </Button>
      </div>
    </form>
  );
}
